import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ADMIN_CAROUSEL_KEYS } from "@shared/constants/queryKeys";
import { apiClient } from "@shared/services/api";
import type {
  HeroCarouselListResponse,
  HeroCarouselPayload,
  HeroCarouselRecord,
  HeroCarouselResponse,
  StandardCarouselListResponse,
  StandardCarouselPayload,
  StandardCarouselRecord,
  StandardCarouselResponse,
  StandardCarouselProductSummary,
} from "@shared/types/carousel";
import type {
  PaginatedResponse,
  PaginationMeta,
} from "@shared/types/pagination";

type ListParams = {
  page?: number;
  limit?: number;
  search?: string;
};

type StandardListParams = ListParams & {
  includeProducts?: boolean;
};

type UpdateEntityArgs<TPayload> = {
  id: string;
  data: TPayload;
};

const buildPagination = (
  meta: PaginationMeta | undefined,
  fallbackCount: number,
  page: number,
  limit: number
): PaginationMeta => {
  const total = meta?.total ?? fallbackCount;
  const currentPage = meta?.page ?? page;
  const pageSize = meta?.limit ?? limit;
  const totalPages =
    meta?.totalPages ?? Math.max(1, Math.ceil(total / pageSize || 1));

  return {
    total,
    page: currentPage,
    limit: pageSize,
    totalPages,
    hasNext: meta?.hasNext ?? currentPage < totalPages,
    hasPrev: meta?.hasPrev ?? currentPage > 1,
  };
};

const buildQueryString = (params: Record<string, string | undefined>): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      searchParams.append(key, value);
    }
  });

  const queryString = searchParams.toString();
  return queryString.length > 0 ? `?${queryString}` : "";
};

export function useAdminHeroCarousels({
  page = 1,
  limit = 10,
  search,
}: ListParams = {}) {
  return useQuery<PaginatedResponse<HeroCarouselRecord>>({
    queryKey: ADMIN_CAROUSEL_KEYS.list({ type: "hero", page, limit, search }),
    queryFn: async (): Promise<PaginatedResponse<HeroCarouselRecord>> => {
      const response = await apiClient.get<HeroCarouselListResponse>(
        "/carousel/hero"
      );

      console.log("📋 Hero Carousel - Data del endpoint:", response.data);

      const items = Array.isArray(response.data) ? response.data : [];

      const filteredItems = search
        ? items.filter((item) =>
            item.name?.toLowerCase().includes(search.toLowerCase())
          )
        : items;

      const startIndex = (page - 1) * limit;
      const paginatedItems = filteredItems.slice(
        startIndex,
        startIndex + limit
      );

      const pagination = buildPagination(
        {
          total: filteredItems.length,
          page,
          limit,
          totalPages: Math.max(1, Math.ceil(filteredItems.length / limit)),
          hasNext: startIndex + limit < filteredItems.length,
          hasPrev: page > 1,
        },
        filteredItems.length,
        page,
        limit
      );

      return {
        data: paginatedItems,
        pagination,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdminStandardCarousels({
  page = 1,
  limit = 10,
  search,
  includeProducts = true,
}: StandardListParams = {}) {
  return useQuery<PaginatedResponse<StandardCarouselRecord>>({
    queryKey: ADMIN_CAROUSEL_KEYS.list({
      type: "standard",
      page,
      limit,
      search,
      includeProducts,
    }),
    queryFn: async (): Promise<PaginatedResponse<StandardCarouselRecord>> => {
      const query = buildQueryString({
        page: String(page),
        limit: String(limit),
        search,
        includeProducts: includeProducts ? "true" : "false",
      });

      const response = await apiClient.get<StandardCarouselListResponse>(
        `/carousel/standard${query}`
      );

      const items = Array.isArray(response.data) ? response.data : [];
      const pagination = buildPagination(
        response.meta,
        items.length,
        page,
        limit
      );

      return {
        data: items,
        pagination,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateHeroCarousel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: HeroCarouselPayload
    ): Promise<HeroCarouselRecord> => {
      const response = await apiClient.post<HeroCarouselResponse>(
        "/carousel/hero",
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CAROUSEL_KEYS.all });
      // Invalidar cache del home porque muestra hero carousel
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
    },
  });
}

export function useUpdateHeroCarousel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: UpdateEntityArgs<HeroCarouselPayload>): Promise<HeroCarouselRecord> => {
      const response = await apiClient.put<HeroCarouselResponse>(
        `/carousel/hero/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CAROUSEL_KEYS.all });
      // Invalidar cache del home porque muestra hero carousel
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
    },
  });
}

export function useToggleHeroCarousel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      isActive,
    }: {
      id: string;
      isActive: boolean;
    }): Promise<HeroCarouselRecord> => {
      const endpoint = isActive
        ? `/carousel/hero/${id}/deactivate`
        : `/carousel/hero/${id}/activate`;
      const response = await apiClient.patch<HeroCarouselResponse>(endpoint);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CAROUSEL_KEYS.all });
      // Invalidar cache del home porque el hero carousel activo puede cambiar
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
    },
  });
}

export function useDeleteHeroCarousel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/carousel/hero/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CAROUSEL_KEYS.all });
      // Invalidar cache del home porque puede afectar el hero carousel activo
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
    },
  });
}

export function useCreateStandardCarousel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: StandardCarouselPayload
    ): Promise<StandardCarouselRecord> => {
      const response = await apiClient.post<StandardCarouselResponse>(
        "/carousel/standard",
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CAROUSEL_KEYS.all });
      // Invalidar cache del home porque muestra standard carousels
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
    },
  });
}

export function useUpdateStandardCarousel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: UpdateEntityArgs<StandardCarouselPayload>): Promise<StandardCarouselRecord> => {
      const response = await apiClient.put<StandardCarouselResponse>(
        `/carousel/standard/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CAROUSEL_KEYS.all });
      // Invalidar cache del home porque muestra standard carousels
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
    },
  });
}

export function useUpdateStandardCarouselProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      productIds,
    }: {
      id: string;
      productIds: string[];
    }): Promise<StandardCarouselProductSummary[]> => {
      const response = await apiClient.put<StandardCarouselResponse>(
        `/carousel/standard/${id}/products`,
        { productIds }
      );
      return response.data.products;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CAROUSEL_KEYS.all });
      // Invalidar cache del home porque los productos del carousel pueden cambiar
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
    },
  });
}

export function useToggleStandardCarousel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      isActive,
    }: {
      id: string;
      isActive: boolean;
    }): Promise<StandardCarouselRecord> => {
      const endpoint = isActive
        ? `/carousel/standard/${id}/deactivate`
        : `/carousel/standard/${id}/activate`;
      const response = await apiClient.patch<StandardCarouselResponse>(endpoint);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CAROUSEL_KEYS.all });
      // Invalidar cache del home porque los carousels activos pueden cambiar
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
    },
  });
}

export function useDeleteStandardCarousel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/carousel/standard/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_CAROUSEL_KEYS.all });
      // Invalidar cache del home porque puede afectar los carousels mostrados
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
    },
  });
}

